// src/pages/GridPage.js
import { DashboardGrid } from '../../components/skins/dashboard-grid.ts';
import { globalState } from "../../framework/globalState.ts";
import { subscribeRegister, sendCommand } from "../../services/mqttClient.ts";
import { FullscreenToggle } from "../../components/utils/fullscreen-toggle.ts";
import { RechartsChartManager } from "../../components/charts/RechartsChartManager.tsx";
import { chartStyles } from "../../components/charts/ChartStyles.ts";
import { groupByDictionaryKey, applyThreshold } from '../../services/registerUtils.ts';
import {
  applySummaryThresholdToElement,
  clearThresholdIndicator,
  computeAggregateFromMap,
  type SummaryThresholdSpec,
} from '../../services/summaryThreshold.ts';

export default function GridPage() {
  console.log("[GridPage] Initializing...");

  const container = document.createElement("div");
  container.classList.add("parent-container");

  // === CSS for Modal ===
  const style = document.createElement("style");
  style.textContent = `
    .confirm-modal { display: none; position: fixed; inset: 0; align-items: center; justify-content: center; background: rgba(0,0,0,0.4); z-index: 9999; }
    .confirm-modal.show { display: flex; }
    .confirm-modal .modal-content { background: #fff; padding: 18px; border-radius: 8px; width: 420px; max-width: 94%; box-shadow: 0 6px 20px rgba(0,0,0,0.15); }
    .confirm-modal .actions { display:flex; gap: 8px; justify-content:flex-end; margin-top:12px; }
    .btn { padding: 8px 12px; border-radius:6px; border: none; cursor:pointer; }
    .btn-primary { background:#2563eb; color:#fff; }
    .btn-secondary { background:#e5e7eb; color:#111; }
  `;
  document.head.appendChild(style);

  // === Load Skin ===
  try {
    container.innerHTML = new DashboardGrid().gridskin().trim();
    console.log("[GridPage] Skin loaded");
  } catch (err) {
    console.error("[GridPage] Skin error:", err);
    container.innerHTML = "<p>Error loading dashboard skin</p>";
    return container;
  }

  const siteWithData = globalState.currentSiteWithData;
  const site = (siteWithData as any)?.site || siteWithData;
  const registers = ((siteWithData as any)?.devices || []).flatMap((d: any) =>
        (d.registers || []).map((r: any) => ({ ...r, deviceName: d.name, deviceId: d._id }))
      );


  const subscriptions = [];

  if (!site?._id) {
    console.warn("[GridPage] No site selected");
    container.innerHTML += "<p class='text-center text-danger'>No site selected.</p>";
    return container;
  }

  console.log(`[GridPage] Site: ${site._id} (${site.name || 'Unnamed'})`);


  // === GRID CONFIG KEYS ===
  const gridConfigKeys = [
    "grid_active_power",
    "grid_reactive_power",
    "grid_voltage",
    "grid_current",
    "grid_frequency",
    "grid_power_factor",
    "grid_energy_today",
    "grid_energy_total"
  ];

  const gridGroups = groupByDictionaryKey(registers);
  const gridSuffixes = new Set();

  gridConfigKeys.forEach(key => {
    gridGroups[key]?.forEach(g => {
      const match = g.name.match(/_(\d+)$/);
      if (match) gridSuffixes.add(match[1]);
    });
  });

  const gridUnits = Array.from(gridSuffixes)
    .sort((a, b) => parseInt(a) - parseInt(b))
    .map((suffix, i) => ({ id: `GRID ${(i + 1).toString().padStart(2, "0")}`, suffix }));

  if (gridUnits.length === 0) gridUnits.push({ id: "GRID 01", suffix: "" });

  console.log(`[GridPage] Detected Grid Units:`, gridUnits.map(u => u.id).join(", "));

  // === CONTROL MAPPING ===
  const controlToMqtt = {
    "Active_Power_Limit": "WP PPC SP",
    "Active_Power_Setpoint": "Power Active Pwr SP",
    "PPC_Active_Power_Mode": "Control Mode",
    "Power_Factor_Setpoint": "PV Meter Power Factor A",
    "Plant_Enable_Command": "Emergency Stop",
  };

  const mqttToControl = Object.fromEntries(
    Object.entries(controlToMqtt).map(([domKey, mqttName]) => [mqttName, domKey])
  );

  // === DYNAMIC TABLE ===
  const gridTable = container.querySelector(".grid-metrics table");
  if (!gridTable) {
    console.error("[GridPage] .grid-metrics table not found");
  } else {
    const thead = gridTable.querySelector("thead tr");
    const tbody = gridTable.querySelector("tbody");
    const tbodyRows = tbody.querySelectorAll("tr");

    // === Add GRID columns ===
    gridUnits.slice(1).forEach(unit => {
      const th = document.createElement("th");
      th.textContent = unit.id;
      thead.appendChild(th);

      tbodyRows.forEach(row => {
        const td = document.createElement("td");
        td.className = "text-right w-100 bg-subcontainer";
        td.textContent = "0.00";
        row.appendChild(td);
      });
    });

    // === Label mapping ===
    const labelMap = {
      "Active Power": "grid_active_power",
      "Reactive Power": "grid_reactive_power",
      "Voltage": "grid_voltage",
      "Current": "grid_current",
      "Frequency": "grid_frequency",
      "Power Factor": "grid_power_factor",
      "Energy Today": "grid_energy_today",
      "Total Energy": "grid_energy_total"
    };

    tbodyRows.forEach(row => {
      const label = row.children[0].textContent.trim();
      const configKey = labelMap[label];
      if (configKey) row.setAttribute("data-config", configKey);
    });
  }

  // === SUMMARY ELEMENTS ===
  const summaryElements = {
    activePower: container.querySelector('[data-register="Grid Active Power"]'),
    voltage: container.querySelector('[data-register="Grid Voltage"]'),
    frequency: container.querySelector('[data-register="Grid Frequency"]'),
    energyToday: container.querySelector('[data-register="Grid Energy Today"]'),
    totalEnergy: container.querySelector('[data-register="Grid Total Energy"]'),
  };

  const summaryMapping = {
    activePower: 'grid_active_power',
    voltage: 'grid_voltage',
    frequency: 'grid_frequency',
    energyToday: 'grid_energy_today',
    totalEnergy: 'grid_energy_total',
  };

  const summarySpec: SummaryThresholdSpec = {
    activePower: { keys: ['GRID_ACTIVE_POWER'], mode: 'sum' },
    voltage: { keys: ['GRID_VOLTAGE'], mode: 'avg' },
    frequency: { keys: ['GRID_FREQUENCY'], mode: 'avg' },
    energyToday: { keys: ['GRID_ENERGY_TODAY'], mode: 'sum' },
    totalEnergy: { keys: ['GRID_ENERGY_TOTAL'], mode: 'sum' },
  };

  const summaryValueMode: Record<string, 'sum' | 'avg'> = {
    activePower: 'sum',
    voltage: 'avg',
    frequency: 'avg',
    energyToday: 'sum',
    totalEnergy: 'sum',
  };

  const summaryValues = {};

  // ========================================
  // INSTANT INITIAL VALUES – NO MORE 0.00 FLASH!
  // ========================================
  gridConfigKeys.forEach(configKey => {
    if (!summaryValues[configKey]) summaryValues[configKey] = {};
    const group = gridGroups[configKey] || [];
    group.forEach(reg => {
      if (reg.value == null) return;
      const value = reg.value as number;
      summaryValues[configKey][reg.name] = value;
      const match = reg.name.match(/_(\d+)$/);
      const idx = match ? match[1] : "";
      const gridIndex = gridUnits.findIndex(u => u.suffix === idx);
      if (gridIndex < 0) return;
      const row = gridTable?.querySelector(`tr[data-config="${configKey}"]`);
      if (row) {
        const targetCell = row.children[2 + gridIndex] as HTMLElement;
        if (targetCell) {
          targetCell.textContent = value.toFixed(2);
          applyThreshold(targetCell, value, reg.meta);
        }
      }
    });
  });

  // Update control register displays (e.g. Power Factor SP, Active Power Limit)
  registers.forEach(register => {
    const label = register.name?.trim();
    const controlKey = mqttToControl[label];
    if (!controlKey) return;

    const displayEl =	container.querySelector(`[data-register="${controlKey}"]`);
    if (displayEl && register.value != null) {
      displayEl.textContent = String(register.value);
    }
  });

  // Force summary update with real data
  requestAnimationFrame(() => {
    updateSummary();
  });

  function updateSummary() {
    Object.entries(summaryMapping).forEach(([key, configKey]) => {
      const el = summaryElements[key] as HTMLElement;
      if (!el) return;
      const group = gridGroups[configKey] || [];
      const value = computeAggregateFromMap(group as any[], summaryValues[configKey] || {}, summaryValueMode[key] || 'sum');
      if (value !== null) {
        el.textContent = value.toFixed(2);
        applySummaryThresholdToElement(el, key, value, registers as any[], summarySpec);
      } else {
        el.textContent = "N/A";
        clearThresholdIndicator(el);
      }
    });
  }

  // === Confirm Modal ===
  const modal = document.createElement("div");
  modal.className = "confirm-modal";
  modal.innerHTML = `
    <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <h3 id="confirm-title">Confirm Action</h3>
      <p id="confirm-message">Are you sure?</p>
      <div style="margin-top:10px;">
        <label for="confirm-new-value">New value</label>
        <input id="confirm-new-value" type="text" style="width:100%; padding:8px; margin-top:6px; border-radius:6px; border:1px solid #d1d5db;" />
      </div>
      <div class="actions">
        <button id="confirm-no" class="btn btn-secondary">Cancel</button>
        <button id="confirm-yes" class="btn btn-primary">Confirm & Write</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  function showConfirm(message, defaultValue, callback) {
    const msgEl = modal.querySelector("#confirm-message");
    const inputEl = modal.querySelector("#confirm-new-value");
    const yes = modal.querySelector("#confirm-yes");
    const no = modal.querySelector("#confirm-no");
    if (!msgEl || !inputEl || !yes || !no) return;

    msgEl.textContent = message;
    inputEl.value = defaultValue ?? "";
    modal.classList.add("show");

    const yesHandler = () => {
      modal.classList.remove("show");
      cleanup();
      callback(true, inputEl.value);
    };
    const noHandler = () => {
      modal.classList.remove("show");
      cleanup();
      callback(false, null);
    };
    function cleanup() {
      yes.removeEventListener("click", yesHandler);
      no.removeEventListener("click", noHandler);
    }
    yes.addEventListener("click", yesHandler, { once: true });
    no.addEventListener("click", noHandler, { once: true });
  }

  // === Edit Button Handlers ===
  container.addEventListener("click", (ev) => {
    const btn = ev.target.closest(".register-edit");
    if (!btn) return;

    const displayKey = btn.dataset.register;
    if (!displayKey) return;

    const mqttName = controlToMqtt[displayKey] || displayKey;
    const displayEl = container.querySelector(`[data-register="${displayKey}"]`);
    const currentVal = displayEl ? displayEl.textContent.trim() : "";
    const isEmergency = mqttName.includes("Emergency") || mqttName.includes("Stop");

    const message = isEmergency
      ? `EMERGENCY: Write to "${mqttName}"? This may stop the plant.`
      : `Write to "${mqttName}"?`;

      showConfirm(message, currentVal, async (confirmed, newValue) => {
        if (!confirmed || newValue === null || newValue.trim() === "") {
          return alert("Please enter a valid value.");
        }

        const mqttName = controlToMqtt[displayKey] || displayKey;
        const controlRegister = registers.find(r => r.name === mqttName);

        if (!controlRegister) {
          console.error(`[GridPage] Control register not found: ${mqttName}`);
          return alert("This control is not available on this site.");
        }

        // Parse value properly
        const valueToSend = newValue.trim();
        const isNumeric = !isNaN(valueToSend) && valueToSend !== "";
        const finalAction = isNumeric ? parseFloat(valueToSend) : valueToSend;

        btn.disabled = true;
        const origText = btn.textContent;
        btn.textContent = "Sending...";

        try {
          // CORRECT sendCommand usage for your system
          sendCommand(
            site._id,
            controlRegister.deviceId,
            controlRegister._id,
            finalAction  // this becomes the "action" field (your backend accepts value here)
          );

          console.log(`[GridPage] Command sent → ${mqttName} = ${finalAction}`);

          // Update UI immediately (optimistic update)
          if (displayEl) {
            displayEl.textContent = String(finalAction);
          }

          btn.textContent = "Success";
          setTimeout(() => {
            btn.textContent = origText;
            btn.disabled = false;
          }, 1500);

        } catch (err) {
          console.error("[GridPage] Command failed:", err);
          alert("Command failed. Check connection or device status.");
          btn.textContent = origText;
          btn.disabled = false;
        }
      });
  });

  // === SUBSCRIBE TO GRID REGISTERS ===
  console.log(`[GridPage] Subscribing to ${gridConfigKeys.length} metric groups`);

  gridConfigKeys.forEach(configKey => {
    const group = gridGroups[configKey] || [];
    if (!summaryValues[configKey]) summaryValues[configKey] = {};
    group.forEach(reg => {
      const unsub = subscribeRegister(site._id, reg.deviceId, reg._id, payload => {
        const raw = payload?.value ?? null;
        const value = typeof raw === 'number' ? raw : null;
        summaryValues[configKey][reg.name] = value;
        const match = reg.name.match(/_(\d+)$/);
        const idx = match ? match[1] : "";
        const gridIndex = gridUnits.findIndex(u => u.suffix === idx);
        if (gridIndex < 0) return;
        const row = gridTable?.querySelector(`tr[data-config="${configKey}"]`);
        if (row && value !== null) {
          const targetCell = row.children[2 + gridIndex] as HTMLElement;
          if (targetCell) {
            targetCell.textContent = value.toFixed(2);
            applyThreshold(targetCell, value, reg.meta);
          }
        }
        updateSummary();
      });
      subscriptions.push(unsub);
    });
  });

  // === CONTROL REGISTERS (Update DOM) ===
  registers.forEach(register => {
    const label = register.name?.trim();
    const controlKey = mqttToControl[label];
    if (!controlKey) return;

    const unsub = subscribeRegister(site._id, register.deviceId, register._id, payload => {
      const value = payload?.value ?? null;
      const displayEl = container.querySelector(`[data-register="${controlKey}"]`);
      if (displayEl && value !== null) {
        displayEl.textContent = String(value);
      }
    });
    subscriptions.push(unsub);
  });

  // === CHARTS ===
  console.log("[GridPage] Initializing charts");
  const chartManager = new RechartsChartManager(site, registers);

  // Energy Chart
  chartManager.createChart("energy_produced_today_vs_total_energy", [], { ...chartStyles.dualAxisEnergyLayout, showControls: true }, container);
  chartManager.bindRegisterToChart("energy_produced_today_vs_total_energy", [
    { configKey: "grid_energy_today", mode: "sum", name: "Daily Energy" },
    { configKey: "grid_energy_total", mode: "sum", name: "Total Energy", line: { color: "#ff8800" } }
  ]);

  // Voltage & Frequency
  chartManager.createChart("voltage_frequency", [], { ...chartStyles.voltageFrequencyLayout, showControls: true }, container);
  chartManager.bindRegisterToChart("voltage_frequency", [
    { configKey: "grid_voltage", mode: "average", name: "Voltage" },
    { configKey: "grid_frequency", mode: "average", name: "Frequency", line: { color: "#ff8800" } }
  ]);

  // === INIT & CLEANUP ===
  requestAnimationFrame(() => {
    console.log("[GridPage] Initializing fullscreen toggle");
    new FullscreenToggle().init();
  });

  container.cleanup = () => {
    console.log(`[GridPage] Cleaning up ${subscriptions.length} subscriptions`);
    subscriptions.forEach(unsub => typeof unsub === "function" && unsub());
    chartManager.cleanup();
  };

  console.log("[GridPage] Initialization complete");
  return container;
}